/**
 * @author: lv.chen
 * @date: 2025/2/19
 * @desc: Pipeline / Change Log页面 Change Detail页签 表格组件
 */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useLatest } from 'ahooks';
import { Table, TableProps, TableColumn, TableAddon } from 'tea-component';
import useTableAutotipAddon from '@gp/hooks/useTableAutotipAddon';
import { getChangeDetailTableRowSpansCfg, formatChangeDetailRecords } from '../../utils';
import { getColumns } from './utils/columns';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type {
  IPipelineChangeDetailVO,
  IPipelineChangeDetailTableRecord,
} from '@gp/types/changeLog';
import { getNumberOfDotUnit } from '@gp/utils/common';
interface IChangeDetailTableProps
  extends IBaseProps,
    Omit<TableProps, 'addons' | 'columns' | 'records'> {
  loading?: boolean;
  error?: Error;
  isShowAll?: boolean;
  data?: IPipelineChangeDetailVO[] | null;
  sort?: SortBy<IPipelineChangeDetailTableRecord>[];
  onSortChange?: (payload: SortBy<IPipelineChangeDetailTableRecord>[]) => void;
  onRetry?: () => void;
  onReset?: () => void;
}

const { mergeable, sortable, injectable, pageable } = Table.addons;

const recordKey = (record?: IPipelineChangeDetailTableRecord) => record?.mock_id || '';

const ChangeDetailTable: React.FC<IChangeDetailTableProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    error = false,
    isShowAll = false,
    data = [],
    sort,
    onSortChange,
    onRetry,
    onReset,
    // expandItemList,
    setExpandItemList,
    tableHeight,
  } = props;

  const expandItemListRef = useRef(props); //
  expandItemListRef.current = props.expandItemList;

  const onSortChangeLatest = useLatest(onSortChange);

  const records = useMemo(() => {
    return formatChangeDetailRecords(loading, data, expandItemListRef.current);
  }, [loading, data, expandItemListRef.current]);

  const handleExpandAll = (isExpand: boolean, item: any) => {
    const newId = item?.project_id || '';
    if (isExpand !== true) {
      const newAddArrTemp = [...expandItemListRef.current, newId];
      const newAddArr = [...new Set(newAddArrTemp)];
      setExpandItemList([...newAddArr]);
    } else {
      const newItem = [...new Set(expandItemListRef.current)];
      const newDeArr = newItem?.filter((itemId: string) => itemId !== newId); // 删除所有'b'元素
      setExpandItemList([...newDeArr]);
    }
  };

  const rowSpanCfg = useMemo(() => {
    return getChangeDetailTableRowSpansCfg(loading, records);
  }, [loading, records]);

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,
    isError: !!error,
    onRetry,
    isFound: !records?.length,
    onClear: onReset,
    isUseResetNodata: true,
  });

  const mergeableAddon = useMemo<TableAddon<IPipelineChangeDetailTableRecord>>(() => {
    return mergeable({
      rowSpan: (columnIndex, recordIndex) => {
        if (columnIndex >= 3) return 1;
        return rowSpanCfg?.[recordIndex]?.[columnIndex] ?? 1;
      },
      colSpan: (columnIndex, recordIndex, context) => {
        if (context?.record?.tab === 'ExpandAll') {
          if (columnIndex === 1) {
            return 6;
          }
          if (columnIndex === 0) {
            return 1;
          }
          return 0;
        }
        return 1;
      },
    });
  }, [rowSpanCfg]);

  const sortableAddon = useMemo<TableAddon<IPipelineChangeDetailTableRecord>>(() => {
    return sortable({
      columns: ['tab', 'update_time'],
      value: sort,
      onChange: (payload) => {
        onSortChangeLatest?.current?.(payload);
      },
    });
  }, [sort]);

  const recordCount = records?.length || 0;
  const pageSizeOptions = [10, 20, 50, 100];

  const addons = useMemo<TableAddon<IPipelineChangeDetailTableRecord>[]>(() => {
    return [
      autotipAddon,
      mergeableAddon,
      sortableAddon,
      injectable({
        row: (props, context) => ({
          style: {
            ...(props.style || {}),
            height: context?.record?.tab === 'ExpandAll' ? 50 : 'auto',
          },
        }),
      }),
      pageable({
        stateTextVisible: true,
        pageSizeVisible: false,
        pageIndexVisible: false,
        jumpVisible: false,
        endJumpVisible: false,
        pageIndex: 1,
        pageSize: recordCount,
        pageSizeOptions,
        recordCount,
        stateText: `${getNumberOfDotUnit(recordCount)} item${recordCount > 1 ? 's' : ''} in total`,
      }),
    ];
  }, [autotipAddon, mergeableAddon, sortableAddon, recordCount]);

  const columns = useMemo<TableColumn<IPipelineChangeDetailTableRecord>[]>(() => {
    return getColumns({ isShowAll, handleExpandAll });
  }, [isShowAll]);

  const isReady = useMemo(() => {
    return loading || Boolean(error);
  }, [loading, error]);

  const styleCur = useMemo(() => {
    const newStyle = !isReady ? { overflow: 'auto', maxHeight: `${tableHeight}px` } : null;
    return {
      ...style,
      ...newStyle,
    };
  }, [isReady, style, tableHeight]);

  return (
    <Table
      // style={styleCur}
      className={classnames(
        'dbg-tab-page__table dbg-sortable-table dbg-autotip-table dbg-cell-expand-table dbg-pipeline-change-log-change-detail-table',
        className,
      )}
      verticalTop
      addons={addons}
      columns={columns}
      recordKey={recordKey}
      records={records}
    />
  );
};

export default ChangeDetailTable;
